import React, { useState, useRef, useEffect, Fragment } from 'react';
import { Box, TextField, Button, Paper, Typography, Avatar, IconButton, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/system';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReactMarkdown from 'react-markdown';
import logo from '../assets/logo.png';
import agentImg from '../assets/agent.webp';
import AudioPlayer, { audioManager } from './AudioPlayer';
import PlayButton from './PlayButton';

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

const Chat = () => {
    const recordStartTimeRef = useRef(0);
    // Track if this is the initial page load
    const isInitialLoad = useRef(true);
    // Track the previous message count to detect newly added messages
    const previousMessageCount = useRef(0);

    // Generate or retrieve user ID
    const [userId] = useState(() => {
        let storedUserId = localStorage.getItem('chatUserId');
        if (!storedUserId) {
            storedUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('chatUserId', storedUserId);
        }
        return storedUserId;
    });

    const [transactionId, setTransactionId] = useState(null);
    const [ttsEnabled, setTtsEnabled] = useState(false);

    // Generate a new transaction ID for each user interaction
    const generateTransactionId = () => {
        return 'txn_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    };

    // Helper function to format time
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Helper function to format date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString([], { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        }
    };

    // Helper function to check if messages are on different days
    const isDifferentDay = (timestamp1, timestamp2) => {
        if (!timestamp1 || !timestamp2) return false;
        const date1 = new Date(timestamp1).toDateString();
        const date2 = new Date(timestamp2).toDateString();
        return date1 !== date2;
    };
    
    // Helper to format welcome message with env vars
    const getWelcomeMessage = () => {
        const rawMessage = process.env.REACT_APP_WELCOME_MESSAGE || "Hi! 👋🏻 Thank you for coming to {company_name}. I'm {agent_name}, your {company_name} AI Agent. How can I assist you today?";
        const companyName = process.env.REACT_APP_COMPANY_NAME || "White Label Club";
        const agentName = process.env.REACT_APP_AGENT_NAME || "Wally";        
        return rawMessage
            .replace(/{company_name}/g, companyName)
            .replace(/{agent_name}/g, agentName);
    };

    const [messages, setMessages] = useState(() => {
        const savedMessages = localStorage.getItem('chatHistory');
        let initialMessages = savedMessages ? JSON.parse(savedMessages) : [{
            text: getWelcomeMessage(),
            sender: 'assistant',
            timestamp: new Date().toISOString()
        }];

        // Sanitize any legacy blob: audio URLs from persisted history
        if (Array.isArray(initialMessages)) {
            const sanitizedMessages = initialMessages.map(msg => {
                try {
                    if (msg && typeof msg.audio === 'string' && msg.audio.startsWith('blob:')) {
                        const { audio, tempAudio, ...rest } = msg;
                        return { ...rest };
                    }
                } catch (_) {}
                return msg;
            });
            // If changes were made, persist the sanitized history immediately
            const changed = sanitizedMessages.some((m, i) => m !== initialMessages[i]);
            if (changed) {
                try {
                    localStorage.setItem('chatHistory', JSON.stringify(sanitizedMessages));
                } catch (e) {
                    console.warn('Failed to persist sanitized chat history:', e);
                }
            }
            initialMessages = sanitizedMessages;
        }

        // Set initial message count
        previousMessageCount.current = initialMessages.length;
        return initialMessages;
    });

    // Generate TTS for welcome message
    const generateWelcomeTTS = async () => {
        try {
            const token = localStorage.getItem('apiToken') || process.env.REACT_APP_API_TOKEN;
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tts/generate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    text: getWelcomeMessage(),
                    voice: process.env.REACT_APP_TTS_ASSISTANT_VOICE || "en-US-JaneNeural"
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                return data.audio_url;
            }
        } catch (error) {
            console.error('Failed to generate welcome TTS:', error);
        }
        return null;
    };
    useEffect(() => {
        // Filter out temporary audio URLs before saving to localStorage
        const messagesToSave = messages.map(msg => {
            // Don't save temporary audio URLs to localStorage
            if (msg.tempAudio) {
                return { ...msg, audio: null };
            }
            return msg;
        });
        localStorage.setItem('chatHistory', JSON.stringify(messagesToSave));
    }, [messages]);

    // Generate TTS for initial welcome message if it doesn't have audio
    useEffect(() => {
        const generateInitialWelcomeTTS = async () => {
            if (messages.length === 1 && messages[0].sender === 'assistant' && !messages[0].audio_url) {
                const audioUrl = await generateWelcomeTTS();
                if (audioUrl) {
                    setMessages(prev => prev.map((msg, index) => 
                        index === 0 ? { ...msg, audio_url: audioUrl } : msg
                    ));
                }
            }
        };
        generateInitialWelcomeTTS();
    }, []);

    // Fetch TTS configuration from backend
    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const token = localStorage.getItem('apiToken') || process.env.REACT_APP_API_TOKEN;
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/config`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (response.ok) {
                    const config = await response.json();
                    setTtsEnabled(config.tts_enabled);
                }
            } catch (error) {
                console.error('Failed to fetch config:', error);
            }
        };
        fetchConfig();
    }, []);

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingAudio, setIsUploadingAudio] = useState(false);
    const uploadAbortRef = useRef(null);
    const textAbortRef = useRef(null);
    const [uploadingTransactionId, setUploadingTransactionId] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Helper function to check if a message is newly added
    const isNewlyAddedMessage = (messageIndex) => {
        return messageIndex >= previousMessageCount.current;
    };

    // Helper: determine if the previous user message was audio
    const didUserAskInAudio = (messageIndex) => {
        const prev = messages[messageIndex - 1];
        if (!prev || prev.sender !== 'user') return false;
        return Boolean(prev.audio || prev.user_audio_url);
    };

    // Update previous message count only when new messages are added
    useEffect(() => {
        if (messages.length > previousMessageCount.current) {
            previousMessageCount.current = messages.length;
        }
    }, [messages.length]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const currentTransactionId = generateTransactionId();
        setTransactionId(currentTransactionId);

        const userMessage = { 
            text: input, 
            sender: 'user',
            transactionId: currentTransactionId,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const controller = new AbortController();
            textAbortRef.current = controller;
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                },
                body: JSON.stringify({ message: input, user_id: userId, transaction_id: currentTransactionId }),
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            
            // Update user message with audio URL if TTS generated one
            if (data.user_audio_url) {
                setMessages(prev => prev.map((msg) => 
                    msg.sender === 'user' && msg.transactionId === currentTransactionId
                        ? { 
                            ...msg, 
                            user_audio_url: data.user_audio_url
                        }
                        : msg
                ));
            }
            
            const assistantMessage = { 
                text: data.response, 
                sender: 'assistant',
                audio_url: data.audio_url,
                transactionId: currentTransactionId,
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            if (error.name === 'AbortError') {
                setMessages(prev => prev.filter(m => !(m.sender === 'user' && m.transactionId === currentTransactionId)));
                setMessages(prev => [...prev, { 
                    text: 'Send canceled.', 
                    sender: 'assistant',
                    timestamp: new Date().toISOString()
                }]);
            } else {
                const errorMessage = { 
                    text: 'Sorry, I encountered an error. Please try again.', 
                    sender: 'assistant',
                    timestamp: new Date().toISOString()
                };
                setMessages(prev => [...prev, errorMessage]);
            }
        } finally {
            setIsLoading(false);
            textAbortRef.current = null;
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 48000
                }
            });
            // Choose a supported audio mime type for MediaRecorder (prefer baseline webm first)
            let mimeType = '';
            if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = 'audio/webm';
            } else if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                mimeType = 'audio/webm;codecs=opus';
            } else if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
                mimeType = 'audio/ogg;codecs=opus';
            } else if (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/mp4')) {
                // Safari may expose mp4
                mimeType = 'audio/mp4';
            }

            let recorder;
            try {
                const options = mimeType ? { mimeType, audioBitsPerSecond: 64000 } : { audioBitsPerSecond: 64000 };
                recorder = new MediaRecorder(stream, options);
            } catch (e) {
                console.warn('MediaRecorder options not supported, falling back:', e);
                recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            }
            const chunks = [];
            let finalized = false;
            let dataEventCount = 0;

            // Microphone health checks
            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                console.log('Mic track state:', {
                    readyState: audioTrack.readyState,
                    muted: audioTrack.muted,
                    enabled: audioTrack.enabled,
                    label: audioTrack.label
                });
                audioTrack.onmute = () => console.warn('Microphone track muted');
                audioTrack.onunmute = () => console.log('Microphone track unmuted');
            }

            // Input level meter using WebAudio
            let audioCtx;
            let analyser;
            let levelTimer;
            let maxRms = 0;
            let pcmBuffers = [];
            let processorNode;
            let workletNode;
            try {
                audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                const source = audioCtx.createMediaStreamSource(stream);
                analyser = audioCtx.createAnalyser();
                analyser.fftSize = 2048;
                source.connect(analyser);
                // Ensure audio context is running
                try {
                    await audioCtx.resume();
                    console.log('AudioContext state after resume:', audioCtx.state);
                } catch (resumeErr) {
                    console.warn('AudioContext resume failed:', resumeErr);
                }
                const buf = new Uint8Array(analyser.fftSize);
                levelTimer = setInterval(() => {
                    analyser.getByteTimeDomainData(buf);
                    // Compute RMS (0..1)
                    let sumSq = 0;
                    for (let i = 0; i < buf.length; i++) {
                        const v = (buf[i] - 128) / 128; // center to [-1,1]
                        sumSq += v * v;
                    }
                    const rms = Math.sqrt(sumSq / buf.length);
                    maxRms = Math.max(maxRms, rms);
                }, 200);

                // Prefer AudioWorklet for PCM capture; fallback to ScriptProcessor if unavailable
                try {
                    await audioCtx.audioWorklet.addModule('/worklets/pcm-processor.js');
                    workletNode = new AudioWorkletNode(audioCtx, 'pcm-processor');
                    workletNode.port.onmessage = (e) => {
                        const frame = e.data;
                        // Copy into our buffers to avoid referencing ephemeral memory
                        pcmBuffers.push(new Float32Array(frame));
                    };
                    source.connect(workletNode);
                    // Do not connect to destination to avoid feedback
                    console.log('AudioWorklet PCM capture initialized');
                } catch (workletErr) {
                    console.warn('AudioWorklet unavailable, using ScriptProcessor fallback:', workletErr);
                    const bufferSize = 4096;
                    processorNode = audioCtx.createScriptProcessor(bufferSize, 1, 1);
                    processorNode.onaudioprocess = (e) => {
                        const input = e.inputBuffer.getChannelData(0);
                        pcmBuffers.push(new Float32Array(input));
                    };
                    source.connect(processorNode);
                    // Do NOT connect processor to destination to avoid audible feedback
                }
            } catch (meterErr) {
                console.warn('Audio meter unavailable:', meterErr);
            }

            const finalizeAndSend = async () => {
                if (finalized) return;
                finalized = true;
                const effectiveType = (recorder && recorder.mimeType) ? recorder.mimeType : 'audio/webm';
                let audioBlob = new Blob(chunks, { type: effectiveType });
                const durationMs = Math.max(0, performance.now() - (recordStartTimeRef.current || performance.now()));
                console.log('Recorded blob details:', {
                    mimeType: effectiveType,
                    size: audioBlob.size,
                    durationMs,
                    chunks: chunks.length,
                    maxRms
                });

                const mt = (effectiveType || '').toLowerCase();
                let ext = '.webm';
                if (mt.includes('ogg')) ext = '.ogg';
                else if (mt.includes('mp4')) ext = '.m4a';
                else if (mt.includes('webm')) ext = '.webm';

                // Fallback to WAV if no encoded chunks produced but PCM captured
                if (audioBlob.size === 0 && pcmBuffers.length > 0) {
                    const wavBlob = (() => {
                        const sr = audioCtx ? audioCtx.sampleRate : 48000;
                        let totalLength = 0;
                        for (let i = 0; i < pcmBuffers.length; i++) totalLength += pcmBuffers[i].length;
                        const pcm = new Int16Array(totalLength);
                        let offset = 0;
                        for (let i = 0; i < pcmBuffers.length; i++) {
                            const buf = pcmBuffers[i];
                            for (let j = 0; j < buf.length; j++) {
                                let s = Math.max(-1, Math.min(1, buf[j]));
                                pcm[offset++] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                            }
                        }
                        const headerSize = 44;
                        const bytes = new ArrayBuffer(headerSize + pcm.byteLength);
                        const view = new DataView(bytes);
                        const writeString = (v, p) => { for (let i = 0; i < v.length; i++) view.setUint8(p + i, v.charCodeAt(i)); };
                        writeString('RIFF', 0);
                        view.setUint32(4, 36 + pcm.byteLength, true);
                        writeString('WAVE', 8);
                        writeString('fmt ', 12);
                        view.setUint32(16, 16, true);
                        view.setUint16(20, 1, true);
                        view.setUint16(22, 1, true);
                        view.setUint32(24, sr, true);
                        view.setUint32(28, sr * 2, true);
                        view.setUint16(32, 2, true);
                        view.setUint16(34, 16, true);
                        writeString('data', 36);
                        view.setUint32(40, pcm.byteLength, true);
                        new Uint8Array(bytes, headerSize).set(new Uint8Array(pcm.buffer));
                        return new Blob([bytes], { type: 'audio/wav' });
                    })();
                    audioBlob = wavBlob;
                    ext = '.wav';
                    console.log('Using WAV fallback blob:', { size: audioBlob.size });
                }

                // Require a minimum duration and input, but accept large WAV fallback even with low RMS
                const hasEnoughSize = audioBlob && audioBlob.size >= 8192; // ~8KB minimum meaningful audio
                const hasEnoughDuration = durationMs >= 500;
                const signalOk = maxRms >= 0.01; // lower threshold to reduce false negatives

                if (!audioBlob || audioBlob.size < 256 || !hasEnoughDuration || (!signalOk && !hasEnoughSize)) {
                    console.warn('Recorded audio blob is empty or too small:', audioBlob?.size);
                    const errorMessage = {
                        text: (!hasEnoughDuration)
                            ? 'Recording was too short. Please hold the mic for at least 1 second and try again.'
                            : (!signalOk && !hasEnoughSize)
                            ? 'No microphone input detected. Please check mic permissions and input device, then try again.'
                            : 'Audio data was too small to process. Please try again and speak closer to the mic.',
                        sender: 'assistant',
                        timestamp: new Date().toISOString()
                    };
                    setMessages(prev => [...prev, errorMessage]);
                stream.getTracks().forEach(track => track.stop());
                try { clearInterval(levelTimer); } catch (_) {}
                try {
                    if (workletNode) {
                        workletNode.port.onmessage = null;
                        workletNode.disconnect();
                    }
                    if (processorNode) {
                        processorNode.disconnect();
                        processorNode.onaudioprocess = null;
                    }
                    audioCtx && audioCtx.close();
                } catch (_) {}
                return;
            }

                const currentTransactionId = generateTransactionId();
                setTransactionId(currentTransactionId);

                const userMessage = { 
                    text: 'Audio message', 
                    sender: 'user',
                    tempAudio: true,
                    transactionId: currentTransactionId,
                    timestamp: new Date().toISOString()
                };
                setMessages(prev => [...prev, userMessage]);

                const formData = new FormData();
                formData.append('audio', audioBlob, `recording${ext}`);
                formData.append('user_id', userId);
                formData.append('transaction_id', currentTransactionId);

                setIsLoading(true);
                setIsUploadingAudio(true);
                setUploadingTransactionId(currentTransactionId);
                const controller = new AbortController();
                uploadAbortRef.current = controller;
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                        },
                        body: formData,
                        signal: controller.signal,
                    });

                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const data = await response.json();
                    if (data.transcript || data.user_audio_url) {
                        setMessages(prev => prev.map((msg) => 
                            msg.sender === 'user' && msg.tempAudio && msg.transactionId === currentTransactionId
                                ? { 
                                    ...msg, 
                                    text: data.transcript || msg.text,
                                    audio: data.user_audio_url ? `${process.env.REACT_APP_API_URL}${data.user_audio_url}` : msg.audio,
                                    tempAudio: false 
                                }
                                : msg
                        ));
                    }
                    const assistantMessage = { 
                        text: data.response, 
                        sender: 'assistant',
                        audio: data.audio_url ? `${process.env.REACT_APP_API_URL}${data.audio_url}` : null,
                        transactionId: currentTransactionId,
                        timestamp: new Date().toISOString()
                    };
                    setMessages(prev => [...prev, assistantMessage]);
                } catch (error) {
                    if (error.name === 'AbortError') {
                        console.warn('Audio upload aborted by user');
                        // Remove temp audio message
                        setMessages(prev => prev.filter(m => !(m.sender === 'user' && m.tempAudio && m.transactionId === currentTransactionId)));
                        // Inform user
                        setMessages(prev => [...prev, { 
                            text: 'Upload canceled.', 
                            sender: 'assistant', 
                            timestamp: new Date().toISOString() 
                        }]);
                    } else {
                        console.error('Error:', error);
                        const errorMessage = { 
                            text: 'Sorry, I encountered an error processing your audio. Please try again.', 
                            sender: 'assistant',
                            timestamp: new Date().toISOString()
                        };
                        setMessages(prev => [...prev, errorMessage]);
                    }
                } finally {
                    setIsLoading(false);
                    setIsUploadingAudio(false);
                    setUploadingTransactionId(null);
                    uploadAbortRef.current = null;
                }

                stream.getTracks().forEach(track => track.stop());
                try { clearInterval(levelTimer); } catch (_) {}
                try {
                    if (workletNode) {
                        workletNode.port.onmessage = null;
                        workletNode.disconnect();
                    }
                    if (processorNode) {
                        processorNode.disconnect();
                        processorNode.onaudioprocess = null;
                    }
                    audioCtx && audioCtx.close();
                } catch (_) {}
            };

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    chunks.push(event.data);
                    dataEventCount += 1;
                }
                // If we've stopped and this is the final chunk, finalize now
                if (recorder.state === 'inactive') {
                    // Give the browser a tiny tick to flush the final buffer
                    setTimeout(finalizeAndSend, 0);
                }
            };

            recorder.onstart = () => {
                console.log('Recorder started', { mimeType: recorder.mimeType });
            };

            recorder.onstop = async () => {
                // Finalization happens in ondataavailable when state becomes 'inactive'
                // This ensures the last chunk is included before building the Blob
                console.log('Recorder stopped', { dataEventCount, chunkCount: chunks.length });
            };

            setMediaRecorder(recorder);
            setAudioChunks(chunks);
            // Use a shorter timeslice to flush data more quickly
            recorder.start(200);
            recordStartTimeRef.current = performance.now();
            setIsRecording(true);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Error accessing microphone. Please check your permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && mediaRecorder.state === 'recording') {
            try {
                // Force the recorder to flush any buffered data before stopping
                mediaRecorder.requestData();
            } catch (e) {
                console.warn('requestData failed or unsupported:', e);
            }
            // Slight delay to allow final ondataavailable to arrive
            setTimeout(() => {
                try {
                    mediaRecorder.stop();
                } catch (stopErr) {
                    console.warn('mediaRecorder.stop error:', stopErr);
                }
            }, 50);
            setIsRecording(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                setInput(prev => prev + '\n');
                return;
            }
            if (!isLoading && input.trim()) {
                handleSend();
            }
        }
    };

    const handleCancelUpload = () => {
        if (isUploadingAudio && uploadAbortRef.current) {
            try {
                uploadAbortRef.current.abort();
            } catch (e) {
                console.warn('Failed to abort upload:', e);
            }
        }
    };

    const handleCancelSend = () => {
        if (!isUploadingAudio && textAbortRef.current) {
            try {
                textAbortRef.current.abort();
            } catch (e) {
                console.warn('Failed to abort send:', e);
            }
        }
    };

    const handleClearHistory = async () => {
        const audioUrl = await generateWelcomeTTS();
        const initialMessage = {
            text: getWelcomeMessage(),
            sender: 'assistant',
            timestamp: new Date().toISOString(),
            audio_url: audioUrl
        };
        setMessages([initialMessage]);
        localStorage.setItem('chatHistory', JSON.stringify([initialMessage]));
        // Stop any playing audio
        audioManager.stopAll();
    };

    const components = {
        a: ({node, ...props}) => (
            <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: '#04C9C8' }}>
                {props.children || 'Link'}
            </a>
        )
    };

    return (
        <Box sx={{
            height: '100vh',
            width: '100vw',
            background: '#e0e0e0',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden'
        }}>
            <Box sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxWidth: '800px',
                width: '100%',
                background: '#f5f5f5'
            }}>
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 2, sm: 3 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 2, sm: 3 },
                        borderRadius: 0,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        position: 'relative',
                        zIndex: 1,
                        flexShrink: 0
                    }}
                >
                    <img 
                        src={logo} 
                        alt="White Label Club" 
                        style={{ 
                            height: window.innerWidth < 600 ? '35px' : '45px',
                            transition: 'transform 0.2s'
                        }} 
                    />
                    <Box sx={{ 
                        flexGrow: 1,
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center'
                    }}>
                        <Typography 
                            variant={{ xs: 'h6', sm: 'h5' }}
                            sx={{ 
                                color: '#333',
                                fontWeight: '600',
                                letterSpacing: '0.5px',
                                fontSize: { xs: '1rem', sm: '1.3rem' },
                                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                                lineHeight: 1.2
                            }}
                        >
                            Wally - AI Assistant
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5
                        }}>
                            <Box sx={{
                                 width: 6,
                                 height: 6,
                                 borderRadius: '50%',
                                 backgroundColor: '#4caf50',
                                 animation: `${pulse} 2s infinite`
                             }} />
                            <Typography 
                                variant="caption"
                                sx={{ 
                                    color: '#4caf50',
                                    fontWeight: '500',
                                    fontSize: { xs: '0.75rem', sm: '0.8rem' }
                                }}
                            >
                                online now
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={handleClearHistory}
                        disabled={isLoading || isRecording}
                        sx={{
                            color: '#f44336',
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                            },
                            '&:disabled': {
                                color: '#ccc'
                            }
                        }}
                        title="Clear Chat History"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Paper>
                <Paper 
                    elevation={0}
                    sx={{
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 0,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e0e0e0',
                        marginTop: '-1px',
                        position: 'relative',
                        flex: 1,
                        overflow: 'hidden'
                    }}
                >
                    {/* Messages Area */}
                    <Box sx={{
                        flexGrow: 1,
                        p: { xs: 1, sm: 1 },
                        pt: { xs: 2, sm: 3 },
                        pb: { xs: 1, sm: 1.5 },
                        overflow: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#a8a8a8',
                        },
                    }}>
                        {messages.map((message, index) => {
                            const showDateSeparator = index === 0 || isDifferentDay(messages[index - 1]?.timestamp, message.timestamp);
                            
                            return (
                                 <Fragment key={index}>
                                    {showDateSeparator && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                my: 2
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    backgroundColor: '#f0f0f0',
                                                    color: '#666',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {formatDate(message.timestamp)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                                            mb: { xs: 0.5, sm: 0.7 },
                                            alignItems: 'flex-start',
                                            gap: 0.3
                                        }}
                                    >
                                {message.sender === 'assistant' && (
                                    <Avatar 
                                        src={agentImg}
                                        sx={{ 
                                            width: { xs: 32, sm: 40 }, 
                                            height: { xs: 32, sm: 40 },
                                            mt: 0.5
                                        }} 
                                    />
                                )}
                                        <Box
                                            sx={{
                                                maxWidth: { xs: '90%', sm: '75%' },
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    p: '10px 10px',
                                                    borderRadius: message.sender === 'user' 
                                                        ? '20px 20px 4px 20px' 
                                                        : '20px 20px 20px 4px',
                                                    backgroundColor: message.sender === 'user' ? '#04C9C8' : '#f5f5f5',
                                                    color: message.sender === 'user' ? 'white' : '#333',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                    border: message.sender === 'assistant' ? '1px solid #e0e0e0' : 'none',
                                                    wordBreak: 'break-word'
                                                }}
                                            >
                                                {message.audio && (
                                                    <Box>
                                                        {/* Display transcription text above audio player for voice messages */}
                                                        {message.text && message.sender === 'user' && (
                                                            <Box sx={{ mb: 1 }}>
                                                                <Typography 
                                                                    component="span" 
                                                                    sx={{ 
                                                                        fontStyle: 'italic',
                                                                        fontSize: '0.75rem',
                                                                        color: message.sender === 'user' ? 'rgba(255,255,255,0.8)' : '#666',
                                                                        display: 'block',
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    Transcription:
                                                                </Typography>
                                                                <Box sx={{ 
                                                                    fontStyle: 'italic',
                                                                    '& *': {
                                                                        padding: 0,
                                                                        margin: 0
                                                                    }
                                                                }}>
                                                                    <div style={{ fontSize: '13px', color: '#999', marginTop: '4px', alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                                                                    <ReactMarkdown components={components}>
                                                                        {message.text}
                                                                    </ReactMarkdown>
                                                                    </div>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        {/* Display text content for assistant messages (always show text even if audio exists) */}
                                                        {message.text && message.sender === 'assistant' && (
                                                            <Box sx={{ mb: 1 }}>
                                                                <ReactMarkdown components={components}>
                                                                    {message.text}
                                                                </ReactMarkdown>
                                                            </Box>
                                                        )}
                                                        <AudioPlayer 
                                            src={message.audio} 
                                            transcription={message.text}
                                            isUserMessage={message.sender === 'user'}
                                            autoPlay={message.sender === 'assistant' && isNewlyAddedMessage(index) && didUserAskInAudio(index)}
                                            userId={userId}
                                        />
                                                    </Box>
                                                )}
                                                {!message.audio && (
                                                    <Box>
                                                        <ReactMarkdown components={components}>
                                                            {message.text}
                                                        </ReactMarkdown>
                                                        {/* Display audio controls for text messages */}
                                                        {message.text && (
                                                            <Box sx={{ mt: 1 }}>
                                                                {/* Show AudioPlayer when TTS is enabled and audio URL exists */}
                                                                {ttsEnabled && (message.audio_url || message.user_audio_url) ? (
                                                                    <AudioPlayer 
                                                                        src={`${process.env.REACT_APP_API_URL}${message.sender === 'user' ? message.user_audio_url : message.audio_url}`}
                                                                        transcription={message.text}
                                                                        isUserMessage={message.sender === 'user'}
                                                                        autoPlay={message.sender === 'assistant' && isNewlyAddedMessage(index) && didUserAskInAudio(index)}
                                                                        userId={userId}
                                                                    />
                                                                ) : (
                                                                    /* Show PlayButton for assistant messages when no audio URL exists or TTS is disabled */
                                                                    message.sender === 'assistant' && (
                                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                                                            <PlayButton 
                                                                                text={message.text}
                                                                                existingAudioUrl={message.audio_url ? `${process.env.REACT_APP_API_URL}${message.audio_url}` : null}
                                                                            />
                                                                        </Box>
                                                                    )
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: '0.5rem',
                                                    color: '#999',
                                                    mt: 0.5,
                                                    alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start'
                                                }}
                                            >
                                                {formatTime(message.timestamp)}
                                            </Typography>
                                        </Box>
                                        {message.sender === 'user' && (
                                            <Avatar sx={{ 
                                                width: { xs: 32, sm: 40 }, 
                                                height: { xs: 32, sm: 40 },
                                                backgroundColor: '#04C9C8',
                                                mt: 0.5
                                            }}>
                                                <PersonIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                                            </Avatar>
                                        )}
                                    </Box>
                                </Fragment>
                             );
                        })}
                        {isLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, alignItems: 'flex-start', gap: 1 }}>
                                <Avatar 
                                    src={agentImg}
                                    sx={{ 
                                        width: { xs: 32, sm: 40 }, 
                                        height: { xs: 32, sm: 40 },
                                        mt: 0.5
                                    }} 
                                />
                                <Box sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: '20px 20px 20px 4px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <CircularProgress size={20} sx={{ color: '#04C9C8' }} />
                                </Box>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Input Area */}
                    <Box sx={{
                        p: { xs: 0.5, sm: 0.8 },
                        backgroundColor: 'white',
                        borderTop: '1px solid #e0e0e0',
                        flexShrink: 0
                    }}>
                        <Box sx={{ my: { xs: 2, sm: 0.3 }, display: 'flex', gap: { xs: 0.3, sm: 0.5 }, alignItems: 'center' }}>
                            <TextField
                                ref={inputRef}
                                fullWidth
                                multiline
                                maxRows={4}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Type your message..."
                                disabled={isLoading || isRecording}
                                variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '15px',
                                        backgroundColor: '#f8f9fa',
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#04C9C8',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#04C9C8',
                                        },
                                    },
                                    '& .MuiInputBase-input': {                                        
                                        py: { xs: 0, sm: 0 },
                                        px: { xs: 0, sm: 0 },                                        
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    },
                                    minHeight: { xs: 48, sm: 56 },
                                    height: { xs: 48, sm: 56 }
                                }}
                            />
                            <IconButton
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={isLoading}
                                sx={{
                                    minWidth: { xs: 48, sm: 56 },
                                    height: { xs: 48, sm: 56 },
                                    width: { xs: 48, sm: 56 },
                                    borderRadius: '50%',
                                    backgroundColor: isRecording ? '#f44336' : '#04c9c8',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: isRecording ? '#d32f2f' : '#0399a8'
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        color: '#666'
                                    }
                                }}
                                title={isRecording ? "Stop Recording" : "Start Recording"}
                            >
                                {isRecording ? <StopIcon /> : <MicIcon />}
                            </IconButton>
                            <Button
                                onClick={handleSend}
                                disabled={isLoading || !input.trim() || isRecording}
                                variant="contained"
                                sx={{
                                    minWidth: { xs: 48, sm: 56 },
                                    height: { xs: 48, sm: 56 },
                                    width: { xs: 48, sm: 56 },
                                    borderRadius: '50%',
                                    backgroundColor: '#04C9C8',
                                     '&:hover': {
                                         backgroundColor: '#0399a8'
                                     }
                                }}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                            </Button>
                            {isUploadingAudio && (
                                <Button
                                    onClick={handleCancelUpload}
                                    title="Cancel Upload"
                                    disabled={false}
                                    variant="contained"
                                    sx={{
                                        minWidth: { xs: 48, sm: 56 },
                                        height: { xs: 48, sm: 56 },
                                        width: { xs: 48, sm: 56 },
                                        borderRadius: '50%',
                                        backgroundColor: '#f44336',
                                        ml: { xs: 0.5, sm: 1 },
                                        '&:hover': {
                                            backgroundColor: '#d32f2f'
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </Button>
                            )}
                            {isLoading && !isUploadingAudio && (
                                <Button
                                    onClick={handleCancelSend}
                                    title="Cancel Send"
                                    disabled={false}
                                    variant="contained"
                                    sx={{
                                        minWidth: { xs: 48, sm: 56 },
                                        height: { xs: 48, sm: 56 },
                                        width: { xs: 48, sm: 56 },
                                        borderRadius: '50%',
                                        backgroundColor: '#f44336',
                                        ml: { xs: 0.5, sm: 1 },
                                        '&:hover': {
                                            backgroundColor: '#d32f2f'
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default Chat;